import React, { useState, useEffect, useCallback } from 'react';
import { Table, Row, Col, Container, Form, InputGroup, Badge, Pagination, Alert } from 'react-bootstrap';
import axios from 'axios';
import moment from 'moment';
import { toast } from 'react-toastify';
import '../../../../custom.css';
import AsyncSelect from 'react-select/async';
import _ from 'lodash';
import { getCurrencySymbol } from '../../common_components/choices';
import FloatingActionButton from '../../common_components/FloatingActionButton';
import PurchaseDetailsModal from '../groceryLists/PurchaseDetailsModal';
import DateFilters from '../../common_components/DateFilters';
import { getPaginationItems } from '../../common_components/getPaginationItems';

const PurchasedList = () => {
    const token = localStorage.getItem('token');
    const [items, setItems] = useState([]);
    const [searchText, setSearchText] = useState('');
    const [debouncedSearchText, setDebouncedSearchText] = useState('');

    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    const [selectedItem, setSelectedItem] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState(null);

    const [showPurchaseModal, setShowPurchaseModal] = useState(false);

    const pageSize = 8;
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearchText(searchText);
        }, 300);

        return () => {
            clearTimeout(handler);
        };
    }, [searchText]);

    const fetchPurchasedItems = useCallback(async (searchTerm, page = 1) => {
        try {
            const params = {
                page_size: pageSize,
                page,
                search: searchTerm || undefined,
                purchased: true,
                purchase_date_from: startDate || undefined,
                purchase_date_to: endDate || undefined,
                category_id: selectedCategory ? selectedCategory.value : undefined
            };

            const response = await axios.get(`${process.env.REACT_APP_BACKEND_API_URL}/highgarden/user-grocery-items/`, {
                params,
                headers: {
                    Authorization: `Token ${token}`,
                }
            });

            setTotalPages(Math.ceil(response.data.count / pageSize));
            setItems(response.data.results);
        } catch (error) {
            console.error('Error fetching purchased items:', error);
            toast.error('Error fetching purchased items');
        }
    }, [token, startDate, endDate, selectedCategory]);

    useEffect(() => {
        fetchPurchasedItems(debouncedSearchText, currentPage);
    }, [fetchPurchasedItems, debouncedSearchText, currentPage]);

    const loadCategoryOptions = _.debounce(async (inputValue, callback) => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_BACKEND_API_URL}/highgarden/grocery-product-categories/`, {
                params: { search: inputValue, page_size: 10 },
                headers: { Authorization: `Token ${token}` },
            });

            const categories = response.data.results.map(category => ({
                label: category.name,
                value: category.id
            }));
            callback(categories);
        } catch (error) {
            console.error('Error loading category options:', error);
            callback([]);
        }
    }, 300);



    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    const handleOpenPurchaseModal = (item) => {
        setSelectedItem(item);
        setShowPurchaseModal(true);
    }

    const handleClosePurchaseModal = () => {
        setSelectedItem(null);
        setShowPurchaseModal(false);
    }

    return (
        <Container style={{ display: 'flex', flexDirection: 'column', height: '100vh', paddingBottom: '20px' }}>
            <Row>
                <Col>
                    <InputGroup>
                        <Form.Control
                            type="text"
                            placeholder="Search..."
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                        />
                    </InputGroup>
                </Col>
            </Row>
            <Row className="mt-3">
                <Col md={4}>
                    <AsyncSelect
                        cacheOptions
                        loadOptions={loadCategoryOptions}
                        defaultOptions
                        placeholder="Filter by category..."
                        value={selectedCategory}
                        onChange={setSelectedCategory}
                        isClearable
                        menuPlacement="auto"  // This ensures the dropdown doesn't get cut off
                        styles={{ menu: (provided) => ({ ...provided, zIndex: 9999 }) }}  // Ensures dropdown is above other elements
                    />
                </Col>
                <Col md={8}>
                    <DateFilters
                        startDate={startDate}
                        setStartDate={setStartDate}
                        endDate={endDate}
                        setEndDate={setEndDate}
                        applyFilters={() => fetchPurchasedItems(debouncedSearchText, currentPage)}
                        clearFilters={() => {
                            setStartDate('');
                            setEndDate('');
                            fetchPurchasedItems('', currentPage);
                        }}
                    />
                </Col>
            </Row>
            <Row className="mt-3" style={{ flex: 1 }}>
                {items.length > 0 ? (
                    <Col className="table-responsive">
                        <Table striped hover>
                            <thead>
                                <tr>
                                    <th style={{ width: '40%' }}>Name</th>
                                    <th style={{ width: '20%' }}>Purchased On</th>
                                    <th style={{ width: '20%' }}>Price</th>
                                    <th style={{ width: '20%' }}>Savings</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map(item => (
                                    <tr
                                        key={item.id}
                                        onClick={() => handleOpenPurchaseModal(item)}
                                        style={{ cursor: "pointer" }}
                                    >
                                        <td>
                                            <div className="d-flex flex-column text-start">
                                                <div className="d-flex align-items-center">
                                                    <Badge bg="info" style={{ marginRight: "5px" }}>
                                                        {item.quantity}x
                                                    </Badge>
                                                    <span>{item.name}</span>
                                                </div>
                                                <div className="text-start" style={{ marginLeft: "35px" }}>
                                                    <Badge bg="secondary">{item.linked_product.name}</Badge>
                                                </div>
                                            </div>
                                        </td>
                                        <td>{moment(item.purchased_on).format("DD-MMM-YYYY")}</td>
                                        <td>
                                            <span style={{ textDecoration: "line-through", marginRight: "5px" }}>
                                                {item.savings &&
                                                    `${getCurrencySymbol(item.savings?.currency)} ${item.savings?.normal_price
                                                    }`}
                                            </span>
                                            {item.purchase_price &&
                                                `${getCurrencySymbol(item.purchase_price_currency)} ${item.purchase_price
                                                }`}
                                        </td>
                                        <td>
                                            <Badge
                                                className={item.savings?.total_savings > 0 ? "bg-success" : "bg-danger"}
                                                style={{ color: "white" }}
                                            >
                                                {item.savings &&
                                                    `${getCurrencySymbol(item.savings?.currency)} ${item.savings?.total_savings
                                                    }`}
                                            </Badge>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>

                        </Table>
                    </Col>
                ) : (
                    <Col>
                        <Alert variant="warning">No items found.</Alert>
                    </Col>
                )}
            </Row>
            <Row>
                <Col>
                    <Pagination>
                        <Pagination.Prev
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                        />
                        {getPaginationItems(currentPage, totalPages, 11, handlePageChange)}
                        <Pagination.Next
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                        />
                    </Pagination>
                </Col>
            </Row>
            <FloatingActionButton
                actions={[
                    {
                        icon: 'bi bi-plus-square',
                        handler: () => console.log('Add item'),
                        color: 'primary',
                        tooltip: 'Add item'
                    }
                ]}
            />

            <PurchaseDetailsModal
                show={showPurchaseModal}
                onHide={handleClosePurchaseModal}
                item={selectedItem}
                refreshList={fetchPurchasedItems}
            />
        </Container>
    );
};

export default PurchasedList;
